import React, { useState,useRef } from 'react';
import { ArrowLeft, HardDrive, Lock, Shield, AlertTriangle,ChevronLeft,ChevronRight, Icon,} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { bucket } from '@lucide/lab';

const S3Page = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const location = useLocation();
  const navigate = useNavigate();
  const { s3Data, region, accountName, duration } = location.state || {};
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
 const CustomTooltip = ({ text, position = 'left' }) => {
     const [showTooltip, setShowTooltip] = useState(false);
   
     return (
       <div className="relative inline-block">
         <div
           onMouseEnter={() => setShowTooltip(true)}
           onMouseLeave={() => setShowTooltip(false)}
           className="cursor-pointer text-xs text-red-600 hover:text-red-800"
         >
           Error details
         </div>
         {showTooltip && (
           <div className={`absolute z-50 w-96 p-3 text-sm bg-gray-900 text-white rounded shadow-lg -mt-1 ${position === 'right' ? 'right-0' : 'left-0'}`}>
             <div className="whitespace-normal break-words">{text}</div>
           </div>
         )}
       </div>
     );
   };

  const LongTextCell = ({ text, maxLength = 30 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);
    const cellRef = useRef(null);
    
    if (!text || text.length <= maxLength) {
      return <span>{text}</span>;
    }
    
    const handleMouseEnter = (e) => {
      setShowTooltip(true);
      setTimeout(() => {
        if (cellRef.current && tooltipRef.current) {
          const cellRect = cellRef.current.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - cellRect.bottom;
          const tooltipHeight = tooltipRect.height;
          
          if (spaceBelow < tooltipHeight) {
            setTooltipPosition({
              top: -tooltipHeight - 5,
              left: 0
            });
          } else {
            setTooltipPosition({
              top: "100%",
              left: 0
            });
          }
        }
      }, 0);
    };
  
    return (
      <div className="relative inline-block">
        <div
          ref={cellRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-pointer border-b border-dotted border-gray-400"
        >
          {text.substring(0, maxLength)}...
        </div>
        {showTooltip && (
          <div 
            ref={tooltipRef}
            className="absolute z-50 w-64 p-2 text-sm bg-gray-900 text-white rounded shadow-lg"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              maxWidth: "250px",
              wordBreak: "break-word"
            }}
          >
            {text}
          </div>
        )}
      </div>
    );
  };
  const ErrorStatus = ({ errorCode, errorMessage }) => {
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          errorCode ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {errorCode ? 'Error' : 'Success'}
        </span>
        {errorMessage && (
          <CustomTooltip text={errorMessage} position="right" />
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#003366]">S3 Activity Details</h1>
        </div>
        <div className="text-sm text-gray-500">
          Last {duration}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-[#003366] mb-4">Activity Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Bucket Changes</div>
            <div className="text-xl font-semibold text-[#003366]">
              {(s3Data?.summary?.createBucketCount || 0) + (s3Data?.summary?.deleteBucketCount || 0)}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Errors</div>
            <div className="text-xl font-semibold text-[#003366]">
              {s3Data?.summary?.errorCount || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Region</div>
            <div className="text-xl font-semibold text-[#003366]">
              {region}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Account</div>
            <div className="text-xl font-semibold text-[#003366]">
              {accountName}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon iconNode={bucket} className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Bucket Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{s3Data?.summary?.createBucketCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{s3Data?.summary?.deleteBucketCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Access Control</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ACL Changes</span>
              <span className="font-semibold">{s3Data?.summary?.putBucketAclCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Policy Changes</span>
              <span className="font-semibold">
                {(s3Data?.summary?.putBucketPolicyCount || 0) + '/' + 
                 (s3Data?.summary?.deleteBucketPolicyCount || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Potential Public Buckets</span>
              <span className="font-semibold">
                { s3Data?.summary?.deletePublicAccessBlockCount || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Configuration</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Encryption Changes</span>
              <span className="font-semibold">{s3Data?.summary?.putBucketEncryptionCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Logging Changes</span>
              <span className="font-semibold">{s3Data?.summary?.putBucketLoggingCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Versioning Changes</span>
              <span className="font-semibold">{s3Data?.summary?.putBucketVersioningCount || 0}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-[#003366] mb-4">S3 Events</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64 whitespace-nowrap">
  Bucket Name
</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source IP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                { s3Data?.data && s3Data.data.length > 0 ? ( s3Data?.data?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
              .map((event,index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.username} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(event.eventtime).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
  <LongTextCell text={event.bucket_name} />
</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={event.eventname} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.eventtype}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.sourceipaddress}
                    </td>
                    <td className="px-6 py-4">
                      <ErrorStatus 
                        errorCode={event.errorCode}
                        errorMessage={event.errorMessage}
                      />
                    </td>
                  </tr>
                ))):
                <tr>
                <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
                    <p className="text-lg font-medium">No data to display</p>
                    <p className="text-sm">No s3 events were found for the selected time period and filters.</p>
                  </div>
                </td>
              </tr>}
              </tbody>
            </table>
          </div>
           {s3Data?.data?.length > 0 && (
                <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center text-sm text-gray-500">
                    <span>Showing {Math.min(s3Data.data.length, (currentPage - 1) * itemsPerPage + 1)} to {Math.min(s3Data.data.length, currentPage * itemsPerPage)} of {s3Data.data.length} results</span>
                    <div className="ml-4">
                      <select 
                        className="border border-gray-300 rounded p-1 text-sm"
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                  {/* First page button */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="First page"
                  >
                    <span className="flex">
                      <ChevronLeft className="h-4 w-4" />
                      <ChevronLeft className="h-4 w-4 -ml-2" />
                    </span>
                  </button>
                  
                  {/* Previous page button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center mx-2">
                    {Array.from({ length: Math.min(5, Math.ceil(s3Data.data.length / itemsPerPage)) }, (_, i) => {
                      const totalPages = Math.ceil(s3Data.data.length / itemsPerPage);
                      let pageNum;
                      
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center rounded ${
                            currentPage === pageNum
                              ? 'bg-[#003366] text-white'
                              : 'text-gray-700 hover:bg-gray-200'
                          }`}
                          aria-label={`Page ${pageNum}`}
                          aria-current={currentPage === pageNum ? "page" : undefined}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === Math.ceil(s3Data.data.length / itemsPerPage)}
                    className={`p-2 rounded ${
                      currentPage === Math.ceil(s3Data.data.length / itemsPerPage)
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.ceil(s3Data.data.length / itemsPerPage))}
                    disabled={currentPage === Math.ceil(s3Data.data.length / itemsPerPage)}
                    className={`p-2 rounded ${
                      currentPage === Math.ceil(s3Data.data.length / itemsPerPage)
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="Last page"
                  >
                    <span className="flex">
                      <ChevronRight className="h-4 w-4" />
                      <ChevronRight className="h-4 w-4 -ml-2" />
                    </span>
                  </button>
                </div>
                </div>
              )}
        </div>
      </div>
    </div>
  );
};

export default S3Page;